import {
  emailCompositionDefaults,
  type EmailCompositionValue,
} from '@kula-ai/components';
import {
  toSelectOptions as toOptions,
  isSameEmailValue,
  type EmailUser,
} from '@/components/shared/email/emailValue';

export type TemplateEmailUser = EmailUser;

/** The Formik slice this composer's consumers read on submit. */
export interface TemplateEmailValues {
  from?: string;
  fromUserObject?: TemplateEmailUser | null;
  cc?: string[];
  bcc?: string[];
  subject?: string;
  body?: string;
  attachments?: unknown[];
}

/**
 * `useEmailField` defaults to `valueKey: 'email'` and these consumers never
 * override it, so from/cc/bcc hold **email addresses**. Options stay keyed by
 * email — keying by id would silently break every send path.
 */
export const toSelectOptions = (users: TemplateEmailUser[] = []) =>
  toOptions(users, 'email');

/**
 * Drops blank entries. A template row can carry empty cc/bcc slots, and
 * `Email` expands those rows whenever the list is non-empty — so a `[null]`
 * would open both rows showing nothing selected.
 */
const toAddressList = (list: unknown): string[] =>
  Array.isArray(list)
    ? list.filter(
        (item): item is string => typeof item === 'string' && item.length > 0
      )
    : [];

/** Seeds the composer from the form's values. */
export const toEmailValue = (
  values: TemplateEmailValues | undefined | null
): EmailCompositionValue => ({
  ...emailCompositionDefaults,
  // Deliberately not seeded — an unmatched address is truthy enough to pass a
  // required check and only fail at the backend. The composer adopts it once
  // the sender list confirms it is selectable.
  from: '',
  // Recipients are fixed by the calling surface.
  to: '',
  cc: toAddressList(values?.cc),
  bcc: toAddressList(values?.bcc),
  subject: values?.subject ?? '',
  content: values?.body ?? '',
  // The template picker is the surface's own row, not kula's.
  template: null,
});

/** Maps a composer snapshot back onto the slice the send paths read. */
export const fromEmailValue = (
  value: EmailCompositionValue,
  resolveUser: (_email: string) => TemplateEmailUser | undefined
): TemplateEmailValues => {
  const sender = resolveUser(value.from);

  return {
    from: value.from,
    // Shared mailboxes are routed by `fromUserObject.isSharedEmail`. Only
    // written when this snapshot names a sender, so an unrelated body edit
    // cannot blank a resolved one.
    ...(sender ? { fromUserObject: sender } : {}),
    cc: value.cc ?? [],
    bcc: value.bcc ?? [],
    subject: value.subject,
    body: value.content,
  };
};

/** Guards against `Email`'s re-emits being stored as new state, which loops. */
export { isSameEmailValue };

/**
 * Picks a sender the From select can actually render: the stored one when it
 * is offered, else the surface default, else nothing.
 */
export const resolveSelectableFrom = (
  from: string | undefined | null,
  fromUsers: TemplateEmailUser[] = [],
  defaultFrom?: string
): string => {
  const canSend = (email?: string | null) =>
    !!email && fromUsers.some((user) => user?.email === email);

  if (canSend(from)) return from as string;
  if (canSend(defaultFrom)) return defaultFrom as string;
  return '';
};

/** Maps template rows to kula `Select` options, carrying the row on `data`. */
export const toTemplateOptions = (
  templates: Array<{ id?: string | number; name?: string }> = []
) =>
  templates
    .filter((template) => template?.id !== undefined && template?.id !== null)
    .map((template) => ({
      label: template.name ?? String(template.id),
      value: String(template.id),
      data: template,
    }));

export type TemplateEmailAttachment = File | Record<string, unknown>;

/** Stable key so the editor's remove button maps a chip back to its entry. */
export const attachmentKey = (
  item: TemplateEmailAttachment,
  index: number
): string =>
  item instanceof File
    ? `file:${item.name}:${item.size}:${item.lastModified}`
    : `id:${(item as { id?: unknown })?.id ?? index}`;

/**
 * Maps the mixed File / stored-row array to kula `Editor` chips.
 *
 * `nonRemovable` rows (e.g. an offer PDF) keep their chip but are reported so
 * the caller can refuse to drop them.
 */
export const toEditorAttachments = (
  items: TemplateEmailAttachment[] = [],
  nonRemovableCount = 0
) =>
  items.map((item, index) => {
    const key = attachmentKey(item, index);
    const base =
      item instanceof File
        ? { name: item.name, size: item.size, mimeType: item.type }
        : {
            name: String(
              (item as Record<string, unknown>).filename ??
                (item as Record<string, unknown>).name ??
                'file'
            ),
            size:
              typeof (item as Record<string, unknown>).byte_size === 'number'
                ? ((item as Record<string, unknown>).byte_size as number)
                : undefined,
            mimeType:
              typeof (item as Record<string, unknown>).content_type === 'string'
                ? ((item as Record<string, unknown>).content_type as string)
                : undefined,
            url:
              typeof (item as Record<string, unknown>).url === 'string'
                ? ((item as Record<string, unknown>).url as string)
                : undefined,
          };

    return { key, ...base, isRemovable: index >= nonRemovableCount };
  });
